import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PROPULSION_TYPE_LABELS, SystemRole } from '@fleet/shared';
import { vehiclesApi, type VehicleDetail, type VehicleListItem } from '../../api/vehicles';
import { vehicleGroupsApi } from '../../api/vehicle-groups';
import { useAuth } from '../../auth/useAuth';
import { AsyncBoundary } from '../../ui/AsyncBoundary';
import { Badge, ColorDot } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { DataTable } from '../../ui/DataTable';
import { ErrorNotice } from '../../ui/ErrorNotice';
import { inputClass } from '../../ui/Field';
import { VehicleForm } from './VehicleForm';

export function VehiclesPage() {
  const auth = useAuth();
  const queryClient = useQueryClient();

  const [includeInactive, setIncludeInactive] = useState(true);
  const [groupFilter, setGroupFilter] = useState('');
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const query = useQuery({
    queryKey: ['vehicles', { includeInactive }],
    queryFn: () => vehiclesApi.list(includeInactive),
  });
  const groups = useQuery({ queryKey: ['vehicle-groups'], queryFn: vehicleGroupsApi.list });

  // Erst beim Bearbeiten geladen: Die Liste kennt die steuerlichen Stammdaten
  // nicht, das Formular braucht sie vollständig.
  const editing = useQuery({
    queryKey: ['vehicle', editingId],
    queryFn: () => vehiclesApi.detail(editingId!),
    enabled: Boolean(editingId),
  });

  const retire = useMutation({
    mutationFn: (id: string) => vehiclesApi.retire(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      await queryClient.invalidateQueries({ queryKey: ['availability'] });
    },
  });

  const filter = (vehicles: VehicleListItem[]) => {
    const term = search.trim().toLowerCase();
    return vehicles.filter(
      (vehicle) =>
        (!groupFilter || vehicle.vehicleGroup.id === groupFilter) &&
        (!term ||
          vehicle.licensePlate.toLowerCase().includes(term) ||
          vehicle.displayName.toLowerCase().includes(term)),
    );
  };

  const canRetire = auth.hasRole(SystemRole.ADMIN);
  const firstGroupId = groups.data?.find((group) => group.isActive)?.id ?? '';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Fahrzeuge</h2>
          <p className="text-sm text-slate-500">
            Stammdaten des Fuhrparks, einschließlich der steuerlich relevanten Angaben.
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => setIsCreating(true)}
          disabled={!groups.data?.some((group) => group.isActive)}
          title={
            groups.data?.some((group) => group.isActive)
              ? undefined
              : 'Zuerst eine Fahrzeuggruppe anlegen — jedes Fahrzeug gehört zu genau einer.'
          }
        >
          Fahrzeug anlegen
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Kennzeichen oder Bezeichnung"
          className={`${inputClass} max-w-xs`}
        />
        <select
          value={groupFilter}
          onChange={(event) => setGroupFilter(event.target.value)}
          className={`${inputClass} max-w-xs`}
          aria-label="Nach Fahrzeuggruppe filtern"
        >
          <option value="">Alle Gruppen</option>
          {groups.data?.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(event) => setIncludeInactive(event.target.checked)}
            className="size-4"
          />
          Stillgelegte anzeigen
        </label>
      </div>

      {retire.isError && <ErrorNotice error={retire.error} />}

      <AsyncBoundary
        query={query}
        empty="Noch kein Fahrzeug erfasst. Sobald eines angelegt ist, erscheint es im Kalender."
      >
        {(vehicles) => (
          <DataTable
            rows={filter(vehicles)}
            rowKey={(vehicle) => vehicle.id}
            rowClassName={(vehicle) => (vehicle.isActive ? '' : 'text-slate-400')}
            columns={[
              {
                header: 'Fahrzeug',
                cell: (vehicle) => (
                  <div className="flex items-center gap-2">
                    <ColorDot color={vehicle.colorHex} />
                    <div>
                      <div className="font-medium">{vehicle.licensePlate}</div>
                      <div className="text-xs text-slate-500">{vehicle.displayName}</div>
                    </div>
                  </div>
                ),
              },
              {
                header: 'Modell',
                cell: (vehicle) => [vehicle.make, vehicle.model].filter(Boolean).join(' ') || '—',
              },
              { header: 'Gruppe', cell: (vehicle) => vehicle.vehicleGroup.name },
              { header: 'Sitze', align: 'right', cell: (vehicle) => vehicle.seats ?? '—' },
              {
                header: 'Merkmale',
                cell: (vehicle) => (
                  <div className="flex flex-wrap gap-1">
                    {vehicle.privateUseAllowed && <Badge tone="amber">Privatnutzung</Badge>}
                    {vehicle.hasDevice && <Badge tone="blue">Logger</Badge>}
                    {!vehicle.isActive && <Badge tone="neutral">Stillgelegt</Badge>}
                  </div>
                ),
              },
              {
                header: '',
                align: 'right',
                cell: (vehicle) => (
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditingId(vehicle.id)}>
                      Bearbeiten
                    </Button>
                    {vehicle.isActive && canRetire && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={retire.isPending}
                        onClick={() => {
                          if (
                            confirm(
                              `${vehicle.licensePlate} außer Dienst stellen?\n\nDas Fahrzeug bleibt samt Historie erhalten und wird nur nicht mehr buchbar. Das Enddatum der Nutzungsdauer wird gesetzt.`,
                            )
                          ) {
                            retire.mutate(vehicle.id);
                          }
                        }}
                      >
                        Stilllegen
                      </Button>
                    )}
                  </div>
                ),
              },
            ]}
          />
        )}
      </AsyncBoundary>

      {isCreating && (
        <VehicleForm fallbackGroupId={firstGroupId} onClose={() => setIsCreating(false)} />
      )}

      {editingId && editing.data && (
        <VehicleForm
          vehicle={editing.data satisfies VehicleDetail}
          fallbackGroupId={firstGroupId}
          onClose={() => setEditingId(null)}
        />
      )}
    </div>
  );
}
